// src/views/ForgotPasswordPage.jsx
import { useState, useMemo, useEffect } from "react";
import PublicTopBar from "./PublicTopBar";
import Footer from "../components/Footer";
import { demanderReinitialisationMotDePasse } from "../services/publicAuthService";
import { publicSiteWebService } from "../services/publicSiteWebService";

export default function ForgotPasswordPage() {
  const [identifiant, setIdentifiant] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [envoye, setEnvoye] = useState(false);

  // ====== Données dynamiques (mêmes sources que ResetPasswordPage) ======
  const [pageData, setPageData] = useState({ parametres: null, contact: null, fonctionnalites: [] });

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await publicSiteWebService.getPublicData();
        setPageData({
          parametres: data?.parametres || null,
          contact: data?.contact || null,
          fonctionnalites: data?.fonctionnalites || [],
        });
      } catch (err) {
      }
    };
    loadData();
  }, []);

  const params = pageData.parametres || {};
  const contact = pageData.contact || {};
  const nomEditeur = params.nom_editeur || "";
  const nomPlateforme = params.nom_plateforme || nomEditeur || "notre plateforme";
  const couleurPrimaire = params.couleur_primaire || "#004080";
  const couleurSecondaire = params.couleur_secondaire || "#0066CC";

  const avantages = useMemo(() => {
    return pageData.fonctionnalites
      .filter((f) => f.actif !== false)
      .slice(0, 3)
      .map((f) => ({
        icone: f.icone || "ti-circle-check",
        texte: f.titre || f.description || "",
        couleur: f.couleur || couleurPrimaire,
      }));
  }, [pageData.fonctionnalites, couleurPrimaire]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!identifiant.trim()) {
      setError("Veuillez saisir votre email ou numéro de téléphone.");
      return;
    }

    setLoading(true);
    try {
      await demanderReinitialisationMotDePasse(identifiant.trim());
      setEnvoye(true);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Une erreur est survenue. Vérifiez l'identifiant saisi et réessayez."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        :root {
          --pr: ${couleurPrimaire};
          --pr-light: ${couleurSecondaire};
          --navy-deep: #052A4E;
          --navy-mid: #0B3E75;
          --gold: #C98A2B;
          --bg-soft: #F5F7FA;
          --ink: #17212E;
          --muted: #64748B;
          --line: #E1E7EF;
        }

        .rp-page { min-height: 100vh; display: flex; flex-direction: column; background: var(--bg-soft); }
        .rp-body { flex: 1; display: flex; padding-top: 72px; padding-bottom: 56px; }

        .rp-brand {
          flex: 1 1 46%;
          position: relative;
          background:
            repeating-linear-gradient(to bottom, rgba(255,255,255,0.035) 0px, rgba(255,255,255,0.035) 1px, transparent 1px, transparent 34px),
            linear-gradient(155deg, var(--navy-deep) 0%, var(--navy-mid) 60%, var(--pr-light) 100%);
          color: #EAF2FB;
          padding: 64px 56px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          overflow: hidden;
        }
        .rp-brand::after {
          content: "";
          position: absolute;
          width: 520px; height: 520px;
          border-radius: 50%;
          background: radial-gradient(circle, ${couleurSecondaire}55 0%, transparent 70%);
          bottom: -220px; left: -160px;
          pointer-events: none;
        }
        .rp-brand-inner { position: relative; max-width: 420px; z-index: 1; animation: rp-fade-up 0.6s ease-out both; }
        .rp-eyebrow {
          display: inline-flex; align-items: center; gap: 8px;
          font-size: 12px; font-weight: 600; letter-spacing: 0.12em; text-transform: uppercase;
          color: var(--gold); margin-bottom: 20px; padding: 6px 12px;
          border: 1px solid rgba(201, 138, 43, 0.35); border-radius: 999px;
          background: rgba(201, 138, 43, 0.08);
        }
        .rp-brand-title { font-weight: 700; font-size: 32px; line-height: 1.28; margin: 0 0 18px; letter-spacing: -0.015em; color: #FFFFFF; }
        .rp-brand-sub { font-size: 15.5px; line-height: 1.65; color: #C6D8ED; margin: 0 0 40px; }
        .rp-benefits { display: flex; flex-direction: column; gap: 18px; margin-bottom: 40px; }
        .rp-benefit { display: flex; align-items: flex-start; gap: 12px; }
        .rp-benefit i { font-size: 18px; color: var(--gold); margin-top: 2px; flex-shrink: 0; }
        .rp-benefit span { font-size: 14.5px; color: #DCE8F6; line-height: 1.5; }
        .rp-quote {
          padding-left: 16px; border-left: 3px solid rgba(201, 138, 43, 0.6);
          font-size: 13.5px; font-style: italic; color: #B7CCE3; line-height: 1.6; max-width: 340px;
        }

        .rp-form-panel { flex: 1 1 54%; position: relative; display: flex; align-items: center; justify-content: center; padding: 48px 32px; }
        .rp-form-panel::before {
          content: ""; position: absolute; left: 0; top: 64px; bottom: 64px; width: 1px;
          background: linear-gradient(to bottom, transparent, rgba(5, 42, 78, 0.12), transparent);
        }
        .rp-form-inner { width: 100%; max-width: 400px; animation: rp-fade-up 0.6s ease-out 0.1s both; }
        .rp-form-title { font-weight: 700; font-size: 24px; color: var(--ink); margin: 0 0 8px; letter-spacing: -0.01em; }
        .rp-form-sub { font-size: 14px; color: var(--muted); line-height: 1.55; margin: 0 0 28px; }

        .rp-error {
          background: #FCEBEB; color: #A32D2D; border-radius: 9px; padding: 11px 14px;
          font-size: 13.5px; margin-bottom: 20px; display: flex; gap: 8px; align-items: flex-start;
          border-left: 3px solid #C0392B; animation: rp-shake 0.35s ease;
        }
        @keyframes rp-shake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-4px); } 75% { transform: translateX(4px); } }

        .rp-success {
          background: #EAF7EF; border-radius: 14px; padding: 32px 24px; text-align: center;
          animation: rp-fade-up 0.5s ease-out both; border: 1px solid rgba(26, 122, 65, 0.12);
        }
        .rp-success i { font-size: 40px; color: #1A7A41; margin-bottom: 12px; display: block; animation: rp-pop 0.5s cubic-bezier(.34,1.56,.64,1) 0.15s both; }
        @keyframes rp-pop { from { transform: scale(0); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        .rp-success-title { font-weight: 700; color: #166034; font-size: 17px; margin-bottom: 6px; }
        .rp-success-sub { font-size: 13.5px; color: #3E7A56; line-height: 1.55; }

        .rp-field { margin-bottom: 18px; }
        .rp-label { display: block; font-size: 13.5px; font-weight: 600; color: var(--ink); margin-bottom: 7px; }
        .rp-input {
          width: 100%; padding: 11px 14px; border: 1.5px solid var(--line); border-radius: 9px;
          font-size: 15px; font-family: inherit; outline: none; box-sizing: border-box;
          color: var(--ink); background: #FBFCFE;
          transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
        }
        .rp-input:hover { border-color: #C7D2E0; }
        .rp-input:focus { border-color: var(--pr-light); background: #FFFFFF; box-shadow: 0 0 0 3.5px ${couleurSecondaire}26; }
        .rp-hint { font-size: 12.5px; color: var(--muted); margin-top: 7px; }

        .rp-btn {
          width: 100%; padding: 13px;
          background: linear-gradient(180deg, var(--pr-light) 0%, var(--pr) 100%);
          color: #fff; border: none; border-radius: 9px; font-size: 15.5px; font-weight: 600;
          font-family: inherit; cursor: pointer; margin-top: 10px;
          display: inline-flex; align-items: center; justify-content: center; gap: 8px; text-align: center;
          transition: transform .12s ease, box-shadow .15s ease;
          box-shadow: 0 4px 12px -2px ${couleurPrimaire}59;
        }
        .rp-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 18px -4px ${couleurPrimaire}73; }
        .rp-btn:disabled { opacity: .72; cursor: default; box-shadow: none; }
        .rp-spin { animation: rp-rotate 0.8s linear infinite; }

        .rp-back { text-align: center; margin-top: 18px; }
        .rp-back a { color: var(--pr); font-size: 13.5px; font-weight: 600; text-decoration: none; }
        .rp-back a:hover { text-decoration: underline; }

        .rp-trust {
          display: flex; align-items: center; gap: 8px; justify-content: center;
          margin-top: 24px; padding-top: 20px; border-top: 1px solid var(--line);
          font-size: 12.5px; color: var(--muted);
        }
        .rp-trust i { font-size: 15px; color: #1A7A41; }

        @keyframes rp-rotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes rp-fade-up { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }

        @media (prefers-reduced-motion: reduce) {
          .rp-brand-inner, .rp-form-inner, .rp-success, .rp-success i, .rp-error { animation: none; }
        }

        @media (max-width: 900px) {
          .rp-body { flex-direction: column; padding-top: 64px; padding-bottom: 40px; }
          .rp-brand { flex: none; padding: 40px 28px 36px; }
          .rp-brand-title { font-size: 26px; }
          .rp-brand-sub { margin-bottom: 24px; }
          .rp-benefits { flex-direction: row; flex-wrap: wrap; gap: 18px; margin-bottom: 24px; }
          .rp-benefit { flex: 1 1 46%; }
          .rp-benefit span { font-size: 13px; }
          .rp-quote { display: none; }
          .rp-form-panel { padding: 36px 20px 48px; }
          .rp-form-panel::before { display: none; }
        }
        @media (max-width: 480px) { .rp-form-title { font-size: 22px; } }
      `}</style>

      <div className="rp-page">
        <PublicTopBar />

        <div className="rp-body">
          <div className="rp-brand">
            <div className="rp-brand-inner">
              <div className="rp-eyebrow">
                <i className="ti ti-lock" />
                Récupération de compte
              </div>

              <h1 className="rp-brand-title">Pas de panique 🔑</h1>

              <p className="rp-brand-sub">
                Indiquez l'email ou le numéro de téléphone associé à votre compte {nomPlateforme}.
                Nous vous envoyons un lien sécurisé pour définir un nouveau mot de passe.
              </p>

              {avantages.length > 0 && (
                <div className="rp-benefits">
                  {avantages.map((a, i) => (
                    <div className="rp-benefit" key={i}>
                      <i className={`ti ${a.icone}`} />
                      <span>{a.texte}</span>
                    </div>
                  ))}
                </div>
              )}

              {nomEditeur && (
                <div className="rp-quote">« Votre accès, retrouvé en quelques secondes. »</div>
              )}
            </div>
          </div>

          <div className="rp-form-panel">
            <div className="rp-form-inner">
              {envoye ? (
                <div className="rp-success" role="status">
                  <i className="ti ti-mail-check" />
                  <div className="rp-success-title">Lien envoyé</div>
                  <div className="rp-success-sub">
                    Si un compte correspond à cet identifiant, un lien de réinitialisation vient d'être envoyé.
                    Vérifiez votre boîte mail ou vos SMS (et vos spams).
                  </div>
                </div>
              ) : (
                <>
                  <h2 className="rp-form-title">Mot de passe oublié ?</h2>
                  <p className="rp-form-sub">
                    Saisissez votre identifiant pour recevoir un lien de réinitialisation.
                  </p>

                  <form onSubmit={handleSubmit} noValidate>
                    {error && (
                      <div className="rp-error" role="alert">
                        <i className="ti ti-alert-circle" />
                        <span>{error}</span>
                      </div>
                    )}

                    <div className="rp-field">
                      <label className="rp-label">Email ou téléphone</label>
                      <input
                        className="rp-input"
                        type="text"
                        placeholder="ex: parent1 ou +226 70 00 00 00"
                        value={identifiant}
                        onChange={(e) => setIdentifiant(e.target.value)}
                        autoFocus
                        autoComplete="username"
                        required
                      />
                      <div className="rp-hint">
                        Utilisez le même identifiant qu'à la connexion.
                      </div>
                    </div>

                    <button type="submit" className="rp-btn" disabled={loading}>
                      {loading ? (
                        <>
                          <i className="ti ti-loader-2 rp-spin" />
                          Envoi...
                        </>
                      ) : (
                        <>
                          <i className="ti ti-send" />
                          Envoyer le lien
                        </>
                      )}
                    </button>
                  </form>

                  <div className="rp-back">
                    <a href="/login">Retour à la connexion</a>
                  </div>

                  <div className="rp-trust">
                    <i className="ti ti-shield-lock" />
                    <span>
                      {contact.email ? `Besoin d'aide ? ${contact.email}` : "Vos informations restent confidentielles"}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}